import { ChevronDown, FileText, MapPin } from "lucide-react";
import type {
  ProcessTraceParams,
  ProcessTraceStatus,
} from "@playbookai/widget-schemas";
import type { ReactElement } from "react";
import { StatusPill } from "../../atoms/StatusPill/index.js";
import { cn } from "../../lib/utils.js";
import type { WidgetBodyProps } from "../types.js";
import {
  processTraceStatusColour,
  processTraceStatusLabel,
} from "./presentation.js";

interface TracePointView {
  readonly code: "POR" | "POO" | "POC";
  readonly name: string;
  readonly primary: string;
  readonly location: string;
  readonly evidence: string;
  readonly status: ProcessTraceStatus;
}

export function ProcessTraceWidget({
  data,
}: WidgetBodyProps<ProcessTraceParams>): React.ReactElement {
  const points: readonly TracePointView[] = [
    {
      code: "POR",
      name: "Point of Recognition",
      primary: data.por.observation,
      location: data.por.location,
      evidence: data.por.evidence,
      status: data.por.status,
    },
    {
      code: "POO",
      name: "Point of Occurrence",
      primary:
        data.poo.divergence.trim() ||
        shouldVersusIs(data.poo.expected, data.poo.actual),
      location: data.poo.location,
      evidence: data.poo.evidence,
      status: data.poo.status,
    },
    {
      code: "POC",
      name: "Point of Cause",
      primary: data.poc.cause,
      location: data.poc.location,
      evidence: data.poc.evidence,
      status: data.poc.status,
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto pe-1">
      {points.map((point, index) => (
        <div key={point.code}>
          <TracePointCard point={point} />
          {index < points.length - 1 ? (
            <div className="flex h-5 items-center justify-center text-pb-text-muted">
              <ChevronDown className="h-4 w-4" aria-hidden />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** A trace point's evidence status as a StatusPill with no dot: a tone, or
 *  tint 7 for the purple "supported" (look row OK-15). */
function TraceStatusPill({
  status,
}: {
  readonly status: ProcessTraceStatus;
}): ReactElement {
  const colour = processTraceStatusColour(status);
  const label = processTraceStatusLabel(status);
  return colour.tint !== undefined ? (
    <StatusPill tint={colour.tint} showDot={false} className="shrink-0">
      {label}
    </StatusPill>
  ) : (
    <StatusPill tone={colour.tone} showDot={false} className="shrink-0">
      {label}
    </StatusPill>
  );
}

function TracePointCard({ point }: { readonly point: TracePointView }) {
  return (
    <section
      className="rounded-md border border-pb-border bg-pb-card-elevated/60 px-3 py-2.5"
      aria-label={`${point.code}: ${point.name}`}
      data-testid={`process-trace-${point.code.toLowerCase()}`}
    >
      <div className="flex min-w-0 items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-xs font-semibold text-pb-text">
              {point.code}
            </span>
            <span className="text-dense-1 text-pb-text-muted">
              {point.name}
            </span>
          </div>
          <p
            className={cn(
              "mt-1 line-clamp-2 break-words text-xs leading-snug",
              point.primary.trim()
                ? "text-pb-text"
                : "italic text-pb-text-muted",
            )}
          >
            {point.primary.trim() || "Not established"}
          </p>
          {point.location.trim() ? (
            <p className="mt-1 flex min-w-0 items-center gap-1 text-dense-1 text-pb-text-muted">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{point.location}</span>
            </p>
          ) : null}
          {point.evidence.trim() ? (
            <p className="mt-1 flex min-w-0 items-center gap-1 text-dense-1 text-pb-text-muted">
              <FileText className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{point.evidence}</span>
            </p>
          ) : null}
        </div>
        <TraceStatusPill status={point.status} />
      </div>
    </section>
  );
}

function shouldVersusIs(expected: string, actual: string): string {
  if (!expected.trim() && !actual.trim()) return "";
  if (!expected.trim()) return `Actual: ${actual}`;
  if (!actual.trim()) return `Expected: ${expected}`;
  return `Expected ${expected}; actual ${actual}`;
}
